import { useEffect, useRef, useState } from 'react';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { loginRequest } from '../authConfig';
import { setBridgeToken } from '../api/portalApi';
import { Icon } from './icons';

interface Props {
  children: React.ReactNode;
}

const DEV_BYPASS = import.meta.env.VITE_DEV_BYPASS_AUTH === 'true';

export default function ProtectedRoute({ children }: Props) {
  const isAuthenticated          = useIsAuthenticated();
  const { instance, inProgress } = useMsal();

  // Bridge state: 'checking' while we attempt the extension handshake,
  // 'ok' if the extension supplied a valid token, 'failed' otherwise.
  const [bridgeState, setBridgeState] = useState<'checking' | 'ok' | 'failed'>('checking');
  const attempted = useRef(false);

  useEffect(() => {
    if (attempted.current) return;
    attempted.current = true;

    // If MSAL already has a cached session, skip the bridge entirely.
    if (isAuthenticated) { setBridgeState('failed'); return; }

    // Read the extension ID the launcher appended to the URL (?ext=<id>).
    const extId = new URLSearchParams(window.location.search).get('ext');
    if (!extId) { setBridgeState('failed'); return; }

    // chrome.runtime is only present in Chrome with the extension installed.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const cr = (window as any).chrome?.runtime;
    if (!cr?.sendMessage) { setBridgeState('failed'); return; }

    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      cr.sendMessage(extId, { type: 'GET_AUTH_TOKEN' }, (response: any) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        if ((window as any).chrome?.runtime?.lastError || !response?.accessToken) {
          setBridgeState('failed');
          return;
        }
        setBridgeToken(response.accessToken);
        // Persist the extension ID so other pages (e.g. WebAutomationPage) can send messages to it
        localStorage.setItem('fint_extension_id', extId);
        setBridgeState('ok');

        // Remove the ?ext= param from the URL bar after a successful handshake.
        const clean = new URL(window.location.href);
        clean.searchParams.delete('ext');
        window.history.replaceState({}, '', clean.toString());
      });
    } catch {
      setBridgeState('failed');
    }
  // Run once on mount — isAuthenticated is only read for the initial value.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Render ────────────────────────────────────────────────────────────────

  if (DEV_BYPASS) return <>{children}</>;

  // Still waiting for the extension to respond.
  if (bridgeState === 'checking') {
    return <div className="loading-screen"><span className="spinner" />Signing in…</div>;
  }

  // Extension supplied a valid token — render protected content directly.
  if (bridgeState === 'ok') return <>{children}</>;

  // MSAL interaction in progress (e.g. popup open, redirect handling).
  if (inProgress !== InteractionStatus.None) {
    return <div className="loading-screen"><span className="spinner" />Signing in…</div>;
  }

  // MSAL has a cached session.
  if (isAuthenticated) return <>{children}</>;

  // No session from either path — show the login screen.
  return (
    <div className="login-screen">
      <div className="login-card">
        <div className="login-logo-wrap">
          <span className="login-mark"><Icon name="sparkle" size={22} /></span>
          <div className="login-wordmark">
            <span className="login-brand">FINT</span><span className="login-product">QualiForge</span>
          </div>
          <div className="login-tagline">FINT · AI-Powered Test Case Engineering</div>
        </div>
        <p className="login-subtitle">Sign in with your Microsoft account to review and approve test cases.</p>
        <button
          className="btn btn-primary login-btn"
          onClick={() => instance.loginRedirect(loginRequest).catch(console.error)}
        >
          Sign in with Microsoft
        </button>
      </div>
    </div>
  );
}
